import React, { Suspense } from "react";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthGuard } from "./components/auth/AuthGuard";
import { TokenRestrictedRoute } from "./components/auth/TokenRestrictedRoute";

// Lazy load all pages
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Campaigns = React.lazy(() => import("./pages/Campaigns"));
const Conversations = React.lazy(() => import("./pages/Conversations"));
const Contacts = React.lazy(() => import("./pages/Contacts"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Disparos = React.lazy(() => import("./pages/Disparos"));
const Templates = React.lazy(() => import("./pages/Templates"));
const RadarAgro = React.lazy(() => import("./pages/RadarAgro"));
const Help = React.lazy(() => import("./pages/Help"));
const Notifications = React.lazy(() => import("./pages/Notifications"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Billing = React.lazy(() => import("./pages/Billing"));
const Auth = React.lazy(() => import("./pages/Auth"));
const Campanhas = React.lazy(() => import("./pages/Campanhas"));
const Garantias = React.lazy(() => import("./pages/Garantias"));
const Cprs = React.lazy(() => import("./pages/Cprs"));
const Assinaturas = React.lazy(() => import("./pages/Assinaturas"));
const TokenAuth = React.lazy(() => import("./pages/TokenAuth").then(m => ({ default: m.TokenAuth })));
const ChatbotConfig = React.lazy(() => import("./pages/ChatbotConfig"));
const FollowUpBoletos = React.lazy(() => import("./pages/FollowUpBoletos"));
const Canhotos = React.lazy(() => import("./pages/Canhotos"));
const Automacoes = React.lazy(() => import("./pages/Automacoes"));
const Boletos = React.lazy(() => import("./pages/Boletos"));
const CarteirasPage = React.lazy(() => import("./pages/Carteiras"));
const CarteiraDetalhePage = React.lazy(() => import("./pages/CarteiraDetalhe"));
const PoliticaPrivacidade = React.lazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeServico = React.lazy(() => import("./pages/TermosDeServico"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/token/:token" element={<TokenAuth />} />
                <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                <Route path="/termos-de-servico" element={<TermosDeServico />} />
                <Route path="/*" element={
                  <AuthGuard>
                    <SidebarProvider>
                      <AppLayout>
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/campanhas" element={<TokenRestrictedRoute><Campanhas /></TokenRestrictedRoute>} />
                            <Route path="/conversas" element={<TokenRestrictedRoute><Conversations /></TokenRestrictedRoute>} />
                            <Route path="/contatos" element={<Contacts />} />
                            <Route path="/carteiras" element={<CarteirasPage />} />
                            <Route path="/carteiras/:id" element={<CarteiraDetalhePage />} />
                            <Route path="/configuracoes" element={<Settings />} />
                            <Route path="/billing" element={<TokenRestrictedRoute><Billing /></TokenRestrictedRoute>} />
                            
                            <Route path="/disparos" element={<Disparos />} />
                            <Route path="/follow-up-boletos" element={<FollowUpBoletos />} />
                            <Route path="/automacoes" element={<Automacoes />} />
                            <Route path="/boletos" element={<Boletos />} />
                            <Route path="/templates" element={<Templates />} />
                            <Route path="/assinaturas" element={<TokenRestrictedRoute><Assinaturas /></TokenRestrictedRoute>} />
                            <Route path="/robo-conversacional" element={<TokenRestrictedRoute><ChatbotConfig /></TokenRestrictedRoute>} />
                            <Route path="/cprs" element={<TokenRestrictedRoute><Cprs /></TokenRestrictedRoute>} />
                            <Route path="/garantias" element={<TokenRestrictedRoute><Garantias /></TokenRestrictedRoute>} />
                            <Route path="/canhotos" element={<Canhotos />} />
                            <Route path="/radar-agro" element={<TokenRestrictedRoute><RadarAgro /></TokenRestrictedRoute>} />
                            <Route path="/ajuda" element={<TokenRestrictedRoute><Help /></TokenRestrictedRoute>} />
                            <Route path="/notificacoes" element={<TokenRestrictedRoute><Notifications /></TokenRestrictedRoute>} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </AppLayout>
                    </SidebarProvider>
                  </AuthGuard>
                } />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
